import React, { useState, useEffect } from 'react';
import { BackButton } from './BackButton';
import { GAME_SCREENS, GameScreen } from '../constants';
import { GameplayScreen } from './GameplayScreen';
import { GameSuccessScreen } from './GameSuccessScreen';
import { HowToPlayModal } from './HowToPlayModal';
import { LoadingSpinner } from './LoadingSpinner';
import { getGameSpecificStorageKey, loadProgressSafely, saveProgressSafely } from './LocalStorage/LocalStorage';
import { saveGameProgress } from './ApiIntegration/ApiIntegration';
import { shuffleArray } from '../utils/shuffleArray';

interface GameData {
  gameSets: Array<{
    setId: number;
    setName: string;
    theme: string;
    backgroundImage: string;
    backgroundEmoji: string;
    kingdomType: string;
    unlockRequirement: number;
    levels: Array<{
      levelId: number;
      levelName: string;
      difficulty: string;
      gridSize: string;
      aiPersonality: string;
      aiEmoji: string;
      specialFeatures: string[];
      victoryReward: string;
      gameBoards: Array<{
        boardId: number;
        boardName: string;
        visualTheme: string;
        playerSymbol: string;
        aiSymbol: string;
        backgroundPattern: string;
        winCondition: string;
        specialRule: string;
      }>;
    }>;
  }>;
}

const QUESTIONS = [
  {
    id: 1,
    title: "Friendly Forest Guardian",
    aiPersonality: "Friendly Guard",
    aiEmoji: "💂‍♀️",
    theme: "forest",
    playerSymbol: "⚔️",
    aiSymbol: "💂‍♀️",
    description: "Battle the gentle guardian of the enchanted forest"
  },
  {
    id: 2,
    title: "Wise Owl Scholar",
    aiPersonality: "Wise Owl Scholar",
    aiEmoji: "🦉",
    theme: "library",
    playerSymbol: "📚",
    aiSymbol: "🦉",
    description: "Challenge the wise keeper of knowledge"
  },
  {
    id: 3,
    title: "Garden Sprite",
    aiPersonality: "Playful Garden Sprite",
    aiEmoji: "🧚🏼",
    theme: "garden",
    playerSymbol: "🌸",
    aiSymbol: "🧚🏼",
    description: "Dance with the magical sprite of the royal gardens"
  },
  {
    id: 4,
    title: "Playful Dragon",
    aiPersonality: "Playful Young Dragon",
    aiEmoji: "🐉",
    theme: "mountain",
    playerSymbol: "⚔️",
    aiSymbol: "🐉",
    description: "Face the mischievous dragon of the crystal caves"
  },
  {
    id: 5,
    title: "Royal Princess",
    aiPersonality: "Royal Princess",
    aiEmoji: "👸🏼",
    theme: "palace",
    playerSymbol: "👑",
    aiSymbol: "👸🏼",
    description: "Compete with the clever princess of the golden palace"
  },
  {
    id: 6,
    title: "Mystical Genie",
    aiPersonality: "Ancient Genie",
    aiEmoji: "🧞‍♂️",
    theme: "desert",
    playerSymbol: "🏺",
    aiSymbol: "🧞‍♂️",
    description: "Match wits with the powerful genie of the lamp"
  },
  {
    id: 7,
    title: "Castle Guardian",
    aiPersonality: "Mighty Castle Guardian",
    aiEmoji: "🏰",
    theme: "castle",
    playerSymbol: "⚔️",
    aiSymbol: "🏰",
    description: "Battle the final guardian of the magical castle"
  }
];

interface UserProgress {
  unlockedLevel: number;
  completedLevels: number[];
  levelScores: Record<number, number>;
  levelStars: Record<number, number>;
  currentSet: number;
}

interface GameStats {
  totalWins: number;
  totalTime: number;
}

const fetchDataWithRetry = async (url: string, retries = 3): Promise<any> => {
  for (let i = 0; i < retries; i++) {
    try {
      const cacheBustUrl = url + (url.includes('?') ? '&' : '?') + 't=' + Date.now();
      const response = await fetch(cacheBustUrl, {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (response.status === 304) {
        console.warn('Got 304 Not Modified, retrying...');
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
        continue;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (fetchError) {
      console.error(`Fetch attempt ${i + 1} failed:`, fetchError);
      if (i === retries - 1) throw fetchError;
      await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, i)));
    }
  }
};

export const MagicalTicTacToeGame: React.FC = () => {
  const [currentScreen, setCurrentScreen] = useState<GameScreen>(GAME_SCREENS.GAMEPLAY);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [gameStats, setGameStats] = useState<GameStats>({ totalWins: 0, totalTime: 0 });
  const [gameStartTime, setGameStartTime] = useState<number>(0);

  const defaultProgress: UserProgress = {
    unlockedLevel: 7,
    completedLevels: [],
    levelScores: {},
    levelStars: {},
    currentSet: 1
  };

  const storageKey = getGameSpecificStorageKey();
  const [userProgress, setUserProgress] = useState<UserProgress>(() => 
    loadProgressSafely(storageKey, defaultProgress)
  );
  const [shuffledQuestions, setShuffledQuestions] = useState(QUESTIONS);

  // Initialize game on mount
  useEffect(() => {
    setShuffledQuestions(shuffleArray([...QUESTIONS]));
    setGameStartTime(Date.now());
    setGameStats({ totalWins: 0, totalTime: 0 });
  }, []);

  // Simulate loading
  useEffect(() => {
    const loadGameData = async () => {
      try {
        setLoading(true);
        
        const dataUrls = [
          'https://kinzy.club/games/game/08-09-25/gjb_2130898747750613926/data_layer/current/data_files/game_data_set_1.json',
          'https://kinzy.club/games/game/08-09-25/gjb_2130898747750613926/data_layer/current/data_files/game_data_set_2.json'
        ];
      } catch (error) {
        console.error('Failed to load game data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadGameData();
  }, []);

  const updateProgress = (newProgress: UserProgress) => {
    setUserProgress(newProgress);
    saveProgressSafely(storageKey, newProgress);
  };

  const handleQuestionComplete = async (score: number, won: boolean) => {
    const newProgress = { ...userProgress };
    
    // Update completed questions
    if (!newProgress.completedLevels.includes(currentQuestion + 1)) {
      newProgress.completedLevels.push(currentQuestion + 1);
    }
    
    // Update scores
    newProgress.levelScores[currentQuestion + 1] = score;
    
    // Update game stats
    if (won) {
      setGameStats(prev => ({ ...prev, totalWins: prev.totalWins + 1 }));
    }
    
    updateProgress(newProgress);
    
    // Don't auto-advance - let the GameplayScreen handle the transition
    // The user will manually proceed when ready
  };

  const handleNextQuestion = () => {
    if (currentQuestion < QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Game completed
      const totalTime = Math.floor((Date.now() - gameStartTime) / 1000);
      setGameStats(prev => ({ ...prev, totalTime }));
      setCurrentScreen('success' as GameScreen);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading magical kingdoms..." />;
  }

  return (
    <div className="relative">
      <BackButton
          onStartGame={() => {
            setShuffledQuestions(shuffleArray([...QUESTIONS]));
            setCurrentScreen(GAME_SCREENS.GAMEPLAY);
            setGameStartTime(Date.now());
            setGameStats({ totalWins: 0, totalTime: 0 });
            setCurrentQuestion(0);
          }}
        currentScreen={currentScreen}
        currentLevel={currentQuestion + 1}
        onBack={(newScreen, newLevel) => {
          setCurrentScreen(newScreen);
          setCurrentQuestion(Math.max(0, newLevel - 1));
        }}
        />

      {currentScreen === GAME_SCREENS.GAMEPLAY && (
        <GameplayScreen
          question={shuffledQuestions[currentQuestion]}
          questionNumber={currentQuestion + 1}
          totalQuestions={shuffledQuestions.length}
          onNextQuestion={handleNextQuestion}
          onQuestionComplete={handleQuestionComplete}
        />
      )}

      {currentScreen === 'success' && (
        <GameSuccessScreen
          totalQuestions={shuffledQuestions.length}
          totalWins={gameStats.totalWins}
          totalTime={gameStats.totalTime}
          onPlayAgain={() => {
            setShuffledQuestions(shuffleArray([...QUESTIONS]));
            setCurrentScreen(GAME_SCREENS.GAMEPLAY);
            setGameStartTime(Date.now());
            setGameStats({ totalWins: 0, totalTime: 0 });
            setCurrentQuestion(0);
          }}
        />
      )}

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        instructions={[
          "Choose to be a brave Knight ⚔️ or a wise Princess 👑",
          "Take turns placing your symbol on the magical grid",
          "Get 3 symbols in a row (horizontal, vertical, or diagonal) to win!",
          "Defeat the AI guardian to unlock new kingdoms",
          "Collect stars and crowns as you master each realm"
        ]}
      />
    </div>
  );
};